import { Injectable } from '@angular/core';
import { BaseService } from '../@base/base.service';
import { Observable } from 'rxjs';
import { DebtHandlingRequest, Debtor, DebtsGroupedResponse, LateInstallmentsRequest, DebtorBankSlipRequest, DebtorBankSlipResponse, DebtServiceHistoryResponse, ListDebtorsRequest, RegisterServiceHistoryRequest, DealersListRequest, DealersListResponse, GetDealerQueryResponse, CreateDealerRequest, UpdateDealerRequest, DealersListBasicResponse } from '../models/dealer/dealer';
import { HttpHeaders } from '@angular/common/http';
import { PaginatorItems } from '../models/table';
import { Payment } from '../models/bank/payment';
import { DatePipe } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class DealersService extends BaseService<Debtor> {

  constructor(private readonly _datePipe: DatePipe) {
    super()
    this.path = "gateway/dealers";
  }

  public getDebtors(dados: ListDebtorsRequest): Observable<PaginatorItems<Debtor>> {

    var queryParam = "";

    dados.periodRanges.forEach(period => {
      queryParam += `periodsRange=${period}&`
    });

    queryParam += `&pageNumber=${dados.pageNumber}`;

    queryParam += `&pageSize=${dados.pageSize}`;

    queryParam += `&clientDocument=${dados.clientDocument}`;

    queryParam += `&identifierCode=${dados.identifierCode}`;

    return this.http.get<PaginatorItems<Debtor>>(`${this.api}${this.path}/debts?${queryParam}`)
  }

  public getDebtorsGrouped(identifierCode: string): Observable<DebtsGroupedResponse> {
    return this.http.get<DebtsGroupedResponse>(`${this.api}${this.path}/debts/grouped-by-range?identifierCode=${identifierCode}`)
  }

  public registerStartDebtHandling(dados: DebtHandlingRequest) : Observable<any> {
    return this.http.post(`${this.api}${this.path}/register-start-debt-handling`, dados)
  }

  public registerEndDebtHandling(dados: DebtHandlingRequest) : Observable<any> {
    return this.http.post(`${this.api}${this.path}/register-end-debt-handling`, dados)
  }

  public insertHistory(dados: RegisterServiceHistoryRequest) : Observable<any> {
    return this.http.post(`${this.api}${this.path}/debtor/${dados.clientDocument}/debts/${dados.contractNumber}/serviceStories`, dados)
  }

  public verifyDebtInServiceHandler(clientDocument: string, contractNumber: string) : Observable<any> {
    return this.http.get(`${this.api}${this.path}/debtor/${clientDocument}/debts/${contractNumber}/inService`)
  }

  public getLateInstallment(dados: LateInstallmentsRequest): Observable<Payment[]> {
    return this.http.get<Payment[]>(`${this.api}${this.path}/debtor/${dados.clientDocument}/debts/${dados.contractNumber}/lateInstalments?identifierCode=${dados.identifierCode}&increaseDays=${dados.increaseDays}`)
  }

  public generateBankSlip(dados: DebtorBankSlipRequest): Observable<Payment[]> {
    return this.http.get<Payment[]>(`${this.api}${this.path}/debtor/${dados.clientDocument}/debts/${dados.contractNumber}/bank-slip`)
  }

  public GeneratePayment(dados: DebtorBankSlipRequest): Observable<DebtorBankSlipResponse> {

    const httpOptions = {
      headers: new HttpHeaders({
        Accept : 'application/octet-stream',
        'Content-Disposition': 'attachment',
        'Response-Type': "arraybuffuer"
      })
    };

    return this.http.post<DebtorBankSlipResponse>(`${this.api}${this.path}/debtor/${dados.clientDocument}/debts/${dados.contractNumber}/bank-slip`, dados, {
      ...httpOptions,
      withCredentials: true
    })
  }

  public getDebtServiceHistories(clientDocument: string, contractNumber: string): Observable<DebtServiceHistoryResponse[]> {
    return this.http.get<DebtServiceHistoryResponse[]>(`${this.api}${this.path}/debtor/${clientDocument}/debts/${contractNumber}/serviceStories`)
  }

  public list(salesPointsListRequest: any, pageNumber: number, pageSize: number): Observable<PaginatorItems<DealersListResponse>> {
    return super.get(`${this.createParam(salesPointsListRequest, pageNumber, pageSize)}`)
  }

  public listBasic(salesPointsListRequest: any, pageNumber: number, pageSize: number): Observable<PaginatorItems<DealersListBasicResponse>> {
    return super.get(`basicInfo${this.createParam(salesPointsListRequest, pageNumber, pageSize)}`)
  }

  public getById(id: number) {
    return super.get<GetDealerQueryResponse>(id);
  }

  public override delete(id: number): Observable<any> {
    return super.delete(id);
  }

  public create(dealer: CreateDealerRequest) {
    return super.post(dealer, "");
  }

  public update(id: number, dealer: UpdateDealerRequest) {
    return super.put(dealer, id);
  }

  // Generate Request and modify request generation
  private createParam(dealersListRequest: DealersListRequest, pageNumber?: number, pageSize?: number): string {
    let query: string = "";

    if (!pageNumber || !pageSize) {
      throw new Error("Não foi especificado uma página e seu tamanho para consulta");
    }

    if (dealersListRequest?.identifierCode ?? "" !== "") {
      query = this.queryWithParameter(query, "IdentifierCode", dealersListRequest.identifierCode);
    }
    if (dealersListRequest?.cnpj != null) {
        query = this.queryWithParameter(query, "cnpj", dealersListRequest.cnpj);
    }
    if (dealersListRequest?.description ?? "" !== "") {
        query = this.queryWithParameter(query, "description", dealersListRequest.description);
    }
    if (dealersListRequest?.segmentId ?? 0 > 0) {
        query = this.queryWithParameter(query, "segmentId", dealersListRequest.segmentId);
    }
    if (dealersListRequest?.economicGroupId ?? 0 > 0) {
        query = this.queryWithParameter(query, "economicGroupId", dealersListRequest.economicGroupId);
    }
    if (dealersListRequest?.active ?? 0 !== 0) {
        query = this.queryWithParameter(query, "active", dealersListRequest.active);
    }
    if (dealersListRequest?.createdBy ?? 0 !== 0) {
        query = this.queryWithParameter(query, "createdBy", dealersListRequest.createdBy);
    }
    if (
        dealersListRequest?.createdAt?.startDate != null &&
        dealersListRequest?.createdAt?.endDate != null
    ) {
        query = this.queryWithParameter(query, "createdAt.StartDate", dealersListRequest.createdAt.startDate.toISOString());
        query = this.queryWithParameter(query, "createdAt.EndDate", dealersListRequest.createdAt.endDate.toISOString());
    }
    if (dealersListRequest?.lastModifiedBy ?? 0 !== 0) {
        query = this.queryWithParameter(query, "lastModifiedBy", dealersListRequest.lastModifiedBy);
    }
    if (
        dealersListRequest?.lastModifiedAt?.startDate != null &&
        dealersListRequest?.lastModifiedAt?.endDate != null
    ) {
        query = this.queryWithParameter(query, "lastModifiedAt.StartDate", dealersListRequest.lastModifiedAt.startDate.toISOString());
        query = this.queryWithParameter(query, "lastModifiedAt.EndDate", dealersListRequest.lastModifiedAt.endDate.toISOString());
    }

    query = this.queryWithParameter(query, "pageNumber", pageNumber);
    query = this.queryWithParameter(query, "pageSize", pageSize);
    return query;
  }

  private queryWithParameter(query: string, name: string, value: any): string {
    if (query == "") {
      query += "?"
    } else {
      query += "&"
    }

    query += `${name}=${value}`;
    return query;
  }
}
